export const copy = {
  hero: {
    eyebrow: 'LIGHTSTRIKE LABS',
    headline: 'Your first hire should have been us.',
    subheadline:
      'AI-first product engineering for ambitious teams. We bring principal-level engineering to companies that need velocity now.',
    cta: 'Book Free Audit',
    trustSignal: 'Trusted by teams pre-seed through Fortune 100',
    availability: 'Currently accepting new clients',
  },

  logoBar: {
    label: 'Our team has built for',
    companies: [
      '3PunchConvo',
      'AdventHealth',
      'Amazon',
      'Cactus',
      'CarMax',
      'DigitalOcean',
      'Django',
      'DOC',
      'Gatsby',
      'Hasura',
      'Kaiber',
      'Lastmile',
      'LeapGrow',
      'Lightmatter',
      'Litera',
      'Microsoft',
      'Nintendo',
      'Observer',
      'RealGraph',
      'RenewaFi',
      'STAR/CHILD',
      'SurePeople',
      'Tamagui',
      'True Homes',
      'Uniswap',
      'USDA',
      'USFS',
      'Vercel',
      'Wellplaece',
      'Worldly',
      'WPGraphQL',
      'XWP',
    ],
  },

  problemAgitate: {
    sectionNumber: '01',
    sectionTitle: 'THE PROBLEM',
    problems: [
      {
        title: 'The Vision Without a Vehicle',
        description:
          "The vision is clear. The market is ready. Yet somewhere between \"here's what I need\" and \"here's what we built\" everything breaks down. You don't need a better spec. You need a team that extracts the spec from your head.",
        agitation: "You're hiring for skills when you need a translator.",
      },
      {
        title: 'The Process Prisoner',
        description:
          "Innovation isn't your problem. Approval is. You've championed three solutions this year. Two died in committee. One is \"still under review.\" The right partner doesn't just deliver; they help you sell internally.",
        agitation: 'You need a vendor who speaks executive, not just engineer.',
      },
      {
        title: 'The Time-Starved Starter',
        description:
          "You've had the vision since 2020. You've started it twice. Once with a freelancer who ghosted, once with an agency that burned through budget. The idea is still good. You just need someone to finally finish it.",
        agitation: "You're not lacking vision. You're lacking bandwidth.",
      },
    ],
  },

  caseStudies: {
    sectionNumber: '02',
    sectionTitle: 'FEATURED WORK',
    header: 'Results that speak for themselves',
    studies: [
      {
        name: '3PunchConvo',
        category: 'Community Platform',
        headline: 'A full-stack community app for combat sports fans',
        description:
          'Built from a single codebase shipping to iOS, Android, and web simultaneously. Forum discussions, fight-pick predictions, credibility scoring, leaderboards, and subscription monetization with comprehensive moderation tooling.',
        results: [
          'Launched on Android, iOS, and web in a single release cycle in < 2 months',
          '40% engagement growth week over week since launch',
          'Zero-downtime deployment from day one',
        ],
        stack: 'React Native · One · Tamagui · Supabase · Hono · Cloudflare Workers · RevenueCat · Klaviyo',
      },
      {
        name: 'STAR/CHILD',
        category: 'AI-Powered Consumer App',
        headline: 'An AI-powered astrology platform built for scale',
        description:
          'Cross-platform app delivering personalized natal charts, AI-generated daily insights, and family-oriented astrology features. Subscription billing across Apple, Google Play, and Stripe for web.',
        results: [
          'From kickoff to Android, iOS, and web in under 4 months',
          'Astrological engine generating 300,000+ unique birth charts',
          'Subscription and AI infrastructure powering 150,000+ personalized insights',
        ],
        stack: 'React Native · Expo · Tamagui · Next.js · Supabase · Payload CMS · RevenueCat · Klaviyo',
      },
    ],
  },

  socialProof: {
    sectionNumber: '03',
    sectionTitle: 'TESTIMONIALS',
    header: 'What our clients say',
    testimonials: [
      {
        quote:
          "Working with Lightstrike has been truly an irreplaceable experience. I've worked with many dev shops, and what I've gotten from Lightstrike really puts them in another category. It genuinely feels like having an in-house team. Every agency will claim to deliver that; most absolutely do not. Lightstrike does.",
        name: 'Jessica Blankenship',
        title: 'Founder & CEO',
        company: 'STAR/CHILD',
        result: '100K+ cross-platform users',
      },
      {
        quote:
          'Lightstrike came in, identified the technical debt holding us back, and rebuilt our architecture from the ground up. The new platform and product strategy they helped us define didn\'t just triple our velocity, it positioned us for acquisition. That foundation made all the difference when it mattered most.',
        name: 'Russell Mikowski',
        title: 'CEO',
        company: 'SurePeople',
        result: 'Acquired April 2026',
      },
      {
        quote:
          "Lightstrike took our raw idea and turned it into a real product strategy. They ran the market analysis, built our MVP, and assembled a product team, all within startup constraints. The foundation they laid carried us through our pivot from HoPPE to Wellplaece and directly enabled our fundraising.",
        name: 'Caen Contee',
        title: 'Founder & CEO',
        company: 'Wellplaece',
        result: 'From MVP to successful fundraise and pivot',
      },
    ],
  },

  team: {
    sectionNumber: '04',
    sectionTitle: 'THE TEAM',
    header: 'Principal-level engineers, not junior contractors',
    description:
      'Every engagement is led by a principal engineer who has built and shipped products at scale. No handoffs to junior devs. No bait and switch.',
    members: [
      {
        name: 'Geoffrey Sechter',
        title: 'Founder & Principal Engineer',
        bio: 'Full-stack product engineer with experience across startups and Fortune 100. Specializes in React, React Native, TypeScript, and AI-first architecture.',
        image: '/team/geoffrey-sechter.webp',
      },
      {
        name: 'Nate Wienert',
        title: 'Principal Engineer',
        bio: 'Creator of open-source frameworks with 20,000+ GitHub stars and millions of monthly downloads. Former staff engineer at Vercel and Uniswap, with a Founders Fund-backed devtools startup.',
        image: '/team/nate-wienert.webp',
      },
      {
        name: 'Brian Gammon',
        title: 'Principal Engineer',
        bio: '15+ years in enterprise technology leadership, from AI-assisted development at scale to solo-bootstrapping a profitable app portfolio. TypeScript, React Native, and cloud-first architecture.',
        image: '/team/brian-gammon.webp',
      },
      {
        name: 'Vladimir Novick',
        title: 'Principal Engineer',
        bio: '20+ years spanning CTO, engineering manager, and architecture roles. Specializes in LLM-based systems, agentic workflows, and turning concepts into production end-to-end.',
        image: '/team/vladimir-novick.webp',
      },
    ],
  },

  transformation: {
    sectionNumber: '05',
    sectionTitle: 'HOW WE WORK',
    header: 'A clear path from conversation to velocity',
    stages: [
      {
        stage: 'DISCOVER',
        subtitle: 'Free Technical Audit',
        timeframe: 'Week 0',
        tagline: 'Every engagement starts with a real conversation.',
        outcome:
          'We review your codebase, assess your architecture, and deliver honest, actionable recommendations. No cost, no commitment.',
        emphasized: true,
      },
      {
        stage: 'IGNITE',
        subtitle: 'Fixed-Scope Sprint',
        timeframe: 'Month 1–2',
        tagline: 'Fixed scope. Fixed price. Surgical.',
        outcome:
          'Critical issues resolved. Architecture corrected. Deployment velocity doubles.',
      },
      {
        stage: 'FORGE',
        subtitle: 'Continuous Partnership',
        timeframe: 'Ongoing',
        tagline: 'Embedded product engineering on retainer.',
        outcome:
          'AI-augmented team shipping at startup speed. Architecture that supports 10x scale.',
        optional: true,
      },
      {
        stage: 'RADIANCE',
        subtitle: 'Full Independence',
        timeframe: "When you're ready",
        tagline: 'Knowledge transfer. Full ownership.',
        outcome:
          'Your team owns every process, pattern, and playbook. New engineers productive in days, not months.',
      },
    ],
  },

  secondaryCta: {
    question: 'Ready to build something that lasts?',
    cta: 'Book Free Audit',
  },

  faq: {
    sectionNumber: '06',
    sectionTitle: 'FAQ',
    questions: [
      {
        question: 'What happens during the free technical audit?',
        answer:
          'We schedule a conversation to understand your product and technical challenges. We review your codebase, identify critical issues and opportunities, and deliver honest, actionable recommendations. No strings attached.',
      },
      {
        question: 'What if my codebase is a total mess?',
        answer:
          "That's where we come in. We've seen ball-of-mud applications with no tests, undocumented legacy systems, vibe-coded prototypes, and everything in between. Our job is to get you back on track.",
      },
      {
        question: "Isn't this just a dev agency?",
        answer:
          "Dev agencies sell billable hours and play labor arbitrage. We sell processes and outcomes with fixed pricing. You know exactly what you're getting and what it costs. In an AI-first world, the old model doesn't work anymore.",
      },
      {
        question: 'What tech stacks do you support?',
        answer:
          "We're strongest with TypeScript, Python, React/Next.js/Vite, React Native/Expo, Deno, Node, PostgreSQL, Supabase, and cloud-native infrastructure. We also work with Go, Java, .NET, and Rust when needed. Good architecture applies everywhere.",
      },
      {
        question: 'How quickly can you start?',
        answer: 'Audits typically begin within one week of our first conversation.',
      },
      {
        question: 'Do I have to commit to a long engagement?',
        answer:
          "No. Every engagement starts with a free audit. After that, you choose your path. Take the recommendations and execute them yourself, or work with us on a fixed-scope project. No lock-in, ever.",
      },
    ],
  },

  footer: {
    copyright: '© 2026 Lightstrike LLC',
    nav: [
      { label: 'Work', href: '/#work' },
      { label: 'Team', href: '/#team' },
      { label: 'FAQ', href: '/#faq' },
      { label: 'Contact', href: 'mailto:sales@lightstrikelabs.com' },
    ],
    legal: [
      { label: 'Privacy Policy', href: '/privacy-policy' },
      { label: 'Terms of Service', href: '/terms-of-service' },
    ],
    social: [
      { label: 'LinkedIn', href: 'https://linkedin.com/company/lightstrike' },
      { label: 'X', href: 'https://x.com/lightstrikelabs' },
    ],
  },

  success: {
    confirmation: 'Your audit request is confirmed.',
    nextSteps: [
      { step: 1, text: '24hr response from our team' },
      { step: 2, text: '30-min discovery call' },
      { step: 3, text: 'Scope confirmation' },
      { step: 4, text: 'Audit begins within one week' },
    ],
  },

  valueStack: {
    sectionNumber: '02',
    sectionTitle: 'THE DIAGNOSIS',
    subtitle: 'Everything you need to understand your codebase',
    items: [
      {
        name: 'Vision-to-Roadmap Translation',
        description: 'Your ideas extracted into clear, executable requirements',
        value: '$3,000',
      },
      {
        name: 'Executive Summary & Business Case',
        description: 'Stakeholder-ready materials with ROI analysis',
        value: '$2,500',
      },
      {
        name: 'Scope & Timeline Reality Check',
        description: 'Honest assessment of what it takes to finish',
        value: '$4,000',
      },
      {
        name: '30/60/90 Day Remediation Roadmap',
        description: 'Actionable plan with clear milestones',
        value: '$2,500',
      },
      {
        name: '60-minute Executive Debrief',
        description: 'Walkthrough with Principal Engineer',
        value: '$500',
      },
    ],
    totalValue: '$12,500',
    yourPrice: '$5,000',
    anchor: 'No obligation to continue. Take the deliverables and run.',
  },

  tiers: {
    diagnosis: {
      name: 'Diagnosis',
      tagline: 'The Diagnostic',
      price: '$5,000',
      duration: '5 business days',
      description: 'Fixed scope, 5 business days',
    },
  },
} as const

export type Copy = typeof copy
